import {Injectable, OnInit} from '@angular/core';
import {Storage} from '@ionic/storage';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {SearchData, AuthData, UserSettings, SmallSelectItem, BaseItem} from './common-datatypes';
import {map, take} from 'rxjs/operators';
import {ActivatedRouteSnapshot} from '@angular/router';
import {AppConfigService} from "./app-config.service";
import {STORAGE_REFRESH_NONCE_KEY, USER_LANGUAGE_KEY} from "../../environments/environment";


const NUM_SEARCH_HISTORY = 10;

@Injectable({
    providedIn: 'root'
})
export class StateStorageService {

    // pure getter and setters -----------------------------------------------------------------
    private _userRoles: SmallSelectItem[] = [];
    get userRoles(): SmallSelectItem[] {
        return this._userRoles;
    }

    set userRoles(value: SmallSelectItem[]) {
        this._userRoles = value;
    }

    private _apiEndPoint: string = '';
    get apiEndPoint(): string {
        return this._apiEndPoint;
    }

    set apiEndPoint(value: string) {
        this._apiEndPoint = value;
    }

    // ------ auth
    private _storedNonce: string = '';
    get storedNonce(): string {
        return this._storedNonce;
    }

    set storedNonce(value: string) {
        this._storedNonce = value;
        this._storage.set(STORAGE_REFRESH_NONCE_KEY, value);
    }

    private _authData: BehaviorSubject<AuthData> =
        new BehaviorSubject(new AuthData());

    get authData$(): Observable<AuthData> {
        return this._authData.asObservable();
    }

    get authData(): AuthData {
        return this._authData.getValue();
    }

    set authData(value: AuthData) {
        this._authData.next(value);
    }

    get loggedIn$(): Observable<boolean> {
        return this._authData.asObservable().pipe(
            map((value: AuthData) => value.loggedIn));
    }

    get loggedIn(): boolean {
        return this._authData.getValue().loggedIn;
    }

    private _userRolesChange: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(false);

    get userRolesChange$(): Observable<boolean> {
        return this._userRolesChange.asObservable();
    }

    set userRolesChange(value: boolean) {
        this._userRolesChange.next(value);
    }

    private _appInitilized: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(false);

    get appInitilized$(): Observable<boolean> {
        return this._appInitilized.asObservable();
    }

    get appInitilized(): boolean {
        return this._appInitilized.getValue();
    }

    // ------ http interceptor
    private _lastHttpStatusCode: BehaviorSubject<number> = new BehaviorSubject(200);

    get lastHttpStatusCode$(): Observable<number> {
        return this._lastHttpStatusCode.asObservable();
    }

    get lastHttpStatusCode(): number {
        return this._lastHttpStatusCode.getValue();
    }

    set lastHttpStatusCode(code: number) {
        this._lastHttpStatusCode.next(code);
    }

    private _lastErrorString: BehaviorSubject<string> = new BehaviorSubject('');

    get lastError$(): Observable<string> {
        return this._lastErrorString.asObservable();
    }

    get lastError(): string {
        return this._lastErrorString.getValue();
    }

    set lastError(msg: string) {
        this._lastErrorString.next(msg);
    }

    private _nonceMissing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    get nonceMissing$(): Observable<boolean> {
        return this._nonceMissing.asObservable();
    }

    get nonceMissing(): boolean {
        return this._nonceMissing.getValue();
    }

    set nonceMissing(value: boolean) {
        this._nonceMissing.next(value);
    }


    // ------ language <--- see constructor
    // user language will be pushed to locale in app.component.ts
    // so primary ist the user language set by the user and stored in ionic storage
    // secondary ist the locale that is read from config.
    private _userLanguage: BehaviorSubject<string> = new BehaviorSubject('');

    get userLanguage$(): Observable<string> {
        return this._userLanguage.asObservable();
    }

    get userLanguage(): string {
        return this._userLanguage.getValue();
    }

    set userLanguage(lang: string) {
        if (lang) {
            this._userLanguage.next(lang);
            this._storage.set(USER_LANGUAGE_KEY, lang);
        }
    }

    private _locale: BehaviorSubject<string> = new BehaviorSubject('');

    get locale$(): Observable<string> {
        return this._locale.asObservable();
    }

    get locale(): string {
        return this._locale.getValue();
    }

    set locale(lang: string) {
        if (lang) this._locale.next(lang);
    }


    // ----- menu state
    private _splitPaneVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    get splitPaneVisible$(): Observable<boolean> {
        return this._splitPaneVisible.asObservable();
    }

    get splitPaneVisible(): boolean {
        return this._splitPaneVisible.getValue();
    }

    set splitPaneVisible(value: boolean) {
        this._splitPaneVisible.next(value);
    }

    private _splitPaneHidden: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    get splitPaneHidden$(): Observable<boolean> {
        return this._splitPaneHidden.asObservable();
    }

    get splitPaneHidden(): boolean {
        return this._splitPaneHidden.getValue();
    }

    set splitPaneHidden(value: boolean) {
        this._splitPaneHidden.next(value);
    }

    // ------ routing
    private _navigation: Subject<ActivatedRouteSnapshot> = new BehaviorSubject(new ActivatedRouteSnapshot());

    get navigation$(): Observable<ActivatedRouteSnapshot> {
        return this._navigation.asObservable();
    }

    set navigation(value: ActivatedRouteSnapshot) {
        this._navigation.next(value);
    }

    // ------ edit in action
    private _editAction: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    get editAction$(): Observable<boolean> {
        return this._editAction.asObservable();
    }

    get editAction(): boolean {
        return this._editAction.getValue();
    }

    set editAction(value: boolean) {
        this._editAction.next(value);
    }

    // ------ edit Valid
    private _editValid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    get editValid$(): Observable<boolean> {
        return this._editValid.asObservable();
    }

    get editValid(): boolean {
        return this._editValid.getValue();
    }

    set editValid(value: boolean) {
        this._editValid.next(value);
    }

    // ----- admin settings
    private _settings: BehaviorSubject<UserSettings> = new BehaviorSubject(new UserSettings(new SmallSelectItem()));

    get settings$(): Observable<UserSettings> {
        return this._settings.asObservable();
    }

    get settings(): UserSettings {
        return this._settings.getValue();
    }

    set settings(data: UserSettings) {
        this._settings.next(data);
    }

    // ----- default sort
    private _defaultSearchSort: BehaviorSubject<SmallSelectItem> = new BehaviorSubject(new SmallSelectItem());

    get defaultSearchSort$(): Observable<SmallSelectItem> {
        return this._defaultSearchSort.asObservable();
    }

    get defaultSearchSort(): SmallSelectItem {
        return this._defaultSearchSort.getValue();
    }

    set defaultSearchSort(data: SmallSelectItem) {
        this._defaultSearchSort.next(data);
    }

    // ----- invnrinput state
    private _invnrInput: BehaviorSubject<string> = new BehaviorSubject('');

    get invnrInput$(): Observable<string> {
        return this._invnrInput.asObservable();
    }

    get invnrInput(): string {
        return this._invnrInput.getValue();
    }

    set invnrInput(value: string) {
        if (value !== this.invnrInput) this._invnrInput.next(value);
    }

    // getter and setters partly in functions -----------------------------------------------------------------

    // ----- search values --> see constructor and extra function getSearchData
    private _searchData: BehaviorSubject<SearchData> = new BehaviorSubject(new SearchData(new SmallSelectItem()));
    private _searchDataId = 0; // id in local cache // TODO has been 1
    private _searchDataArray: Array<SearchData> = [];
    private _searchDataPointer = 0; // pointer in local cache

    get searchData$(): Observable<SearchData> {
        return this._searchData.asObservable();
    }

    get searchData(): SearchData {
        return this._searchData.getValue();
    }

    set searchData(data: SearchData) {
        const current = this._searchData.getValue();
        if (current && current.isSame(data)) return;
        this.setSearchData(data);
    }

    // constructor -----------------------------------------------------------------
    constructor(private _storage: Storage, private config: AppConfigService) {
        const initialSearchData = new SearchData(this.defaultSearchSort);
        initialSearchData.id = this._searchDataId++;
        this._searchDataArray.push(initialSearchData);
        for (let i = 1; i < NUM_SEARCH_HISTORY; i++) this._searchDataArray.push(new SearchData(this.defaultSearchSort));
        this.setSearchData(initialSearchData);

        // default Search Sort will not be available on construction, count 1 is empty behavioursubject default
        this.defaultSearchSort$.pipe(take(2)).subscribe((defaultSort: SmallSelectItem) => {
            if (defaultSort.id === '') return;
            this._searchDataArray.forEach((searchData: SearchData) => {
                searchData.simpleSort.reset(defaultSort);
                searchData.sort.reset(defaultSort);
            });
        });
    }

    public getSearchData(id: number): SearchData {
        for (let i = 0; i < this._searchDataArray.length; i++) {
            if (this._searchDataArray[i].id === id) {
                this._searchData.next(this._searchDataArray[i]);
                return this._searchDataArray[i];
            }
        }
        return new SearchData(new SmallSelectItem());
    }

    public setSearchCount(id: number, count: number): void {
        for (let i = 0; i < this._searchDataArray.length; i++) {
            if (this._searchDataArray[i].id === id) {
                this._searchDataArray[i].countResults = count;
                this._searchData.next(this._searchDataArray[i]);
                break;
            }
        }
    }

    private setSearchData(data: SearchData): void {
        let inCache: number = BaseItem.UNDEFINED_ID;
        for (let i = 0; i < this._searchDataArray.length; i++) {
            if (this._searchDataArray[i].id && this._searchDataArray[i].isSame(data)) {
                inCache = i;
                break;
            }
        }
        if (inCache !== BaseItem.UNDEFINED_ID) {
            this._searchData.next(this._searchDataArray[inCache]);
        } else {
            data.id = this._searchDataId++;
            // make copy of data
            const newData = new SearchData(new SmallSelectItem());
            newData.updateFromJson(data);
            this._searchDataArray[this._searchDataPointer] = newData;
            this._searchData.next(this._searchDataArray[this._searchDataPointer]);
            if (++this._searchDataPointer >= this._searchDataArray.length) this._searchDataPointer = 0;
        }
    }


}
